import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Button } from "components/atoms";
import {
  DocumentUploadModal,
  GeneralSelectInput,
  NumberInput,
  MessageTextAreaInput,
  MultiChoiceCheckboxGroup,
  RadioButtonGroup,
  DatePickerInput,
  TextInput,
  TabbedFormToggle,
} from "components/molecules";
import { useUserData, useUpload, useFileValidation } from "hooks";
import { documentService } from "services";
import { useCurrency } from "contexts";
import {
  GenuineStudentFormValues,
  primaryFundingOptions,
  sponsorRelationshipOptions,
  financialObligationTypesOptions,
  incomeGeneratingAssetTypesOptions,
  researchConductedFullOptions,
  accommodationTypeOptions,
  testOptions,
  gapActivityOptions,
  preparationActivitiesOptions,
  understandingLevelOptions,
  healthInsuranceProviderOptions,
  FileWithType,
  countriesFlat,
} from "types";

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set(["genuineStudentData"]),
};

const disabledOptions = ["TOEFL", "CAE", "CPE"];

const getTestOptions = () => {
  return testOptions.map((option: any) => ({
    ...option,
    isDisabled: disabledOptions.includes(option.value),
  }));
};

interface GSFinancialPreparationSectionProps {
  currentSubStage?: string;
}

const GSFinancialPreparationSection: React.FC<
  GSFinancialPreparationSectionProps
> = ({ currentSubStage = "funding" }) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<GenuineStudentFormValues>();

  const { allCurrencies } = useCurrency();

  const currencyOptions = allCurrencies.map((code) => ({
    label: code,
    value: code,
  }));

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    bankStatements?: { id: string; fileName: string };
    loanDocuments?: { id: string; fileName: string };
    scholarshipDocuments?: { id: string; fileName: string };
    sponsorIdentity?: { id: string; fileName: string };
    relationshipEvidence?: { id: string; fileName: string };
    sponsorLetter?: { id: string; fileName: string };
    sponsorCitizenship?: { id: string; fileName: string };
    sponsorVisa?: { id: string; fileName: string };
    sponsorIncomeTax?: { id: string; fileName: string };
    trainingCertificates?: { id: string; fileName: string };
    oshcReceipt?: { id: string; fileName: string };
    dependentOSHC?: { id: string; fileName: string };
  }>({});
  const userDataHook = useUserData();
  const { userData } = userDataHook;

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024,
      enableMagicNumberValidation: false,
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleFileRemove,
    handleError,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: false,
    customUploadMessage: "Documents uploaded successfully.",
  });

  const financialPreparationTypes = [
    "BANK_STATEMENTS",
    "LOAN_DOCUMENTS",
    "SCHOLARSHIP_DOCUMENTS",
    "SPONSOR_IDENTITY",
    "RELATIONSHIP_EVIDENCE",
    "SPONSOR_LETTER",
    "SPONSOR_CITIZENSHIP",
    "SPONSOR_VISA",
    "SPONSOR_INCOME_TAX",
    "TRAINING_CERTIFICATES",
    "OSHC_RECEIPT",
    "DEPENDENT_OSHC",
  ];

  useEffect(() => {
    if (userData?.id) {
      documentService
        .fetchDocumentsWithMapping(financialPreparationTypes)
        .then(setUploadedDocuments);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const handleUploadWithRefresh = async (files: FileWithType[]) => {
    try {
      await handleFileUpload(files);
      setTimeout(async () => {
        if (userData?.id) {
          const docs = await documentService.fetchDocumentsWithMapping(
            financialPreparationTypes,
          );

          setUploadedDocuments(docs);
        }
      }, 1000);
      return { files, results: [] };
    } catch (error) {
      console.error("Upload failed:", error);
      return { files: [], results: [] };
    }
  };

  const getDocumentFields = (filterByStage?: string) => {
    const allDocuments = [
      {
        id: "bankstatements",
        label: "Bank Statements",
        description: "Bank statements (minimum 6 months)",
        accept: ".pdf,.jpg,.jpeg",
        required: true,
        uploaded: !!uploadedDocuments.bankStatements,
        stage: "funding",
      },
      {
        id: "loandocuments",
        label: "Loan Approval Letter",
        description: "Official loan approval letter",
        accept: ".pdf,.doc,.docx",
        required:
          values.primaryFundingSource === "government_loan" ||
          values.primaryFundingSource === "bank_loan",
        uploaded: !!uploadedDocuments.loanDocuments,
        stage: "funding",
      },
      {
        id: "scholarshipdocuments",
        label: "Scholarship Documents",
        description: "Official scholarship/grant award letter",
        accept: ".pdf,.doc,.docx",
        required: values.primaryFundingSource === "scholarship",
        uploaded: !!uploadedDocuments.scholarshipDocuments,
        stage: "funding",
      },
      {
        id: "sponsoridentity",
        label: "Sponsor Identity",
        description:
          "Sponsor's identity documents and financial capacity proof",
        accept: ".pdf,.doc,.docx,.jpg,.jpeg",
        required: values.hasFinancialSponsors === true,
        uploaded: !!uploadedDocuments.sponsorIdentity,
        stage: "funding",
      },
      {
        id: "relationshipevidence",
        label: "Relationship Evidence",
        description: "Proof of relationship to financial sponsor",
        accept: ".pdf,.doc,.docx,.jpg,.jpeg",
        required: values.hasFinancialSponsors === true,
        uploaded: !!uploadedDocuments.relationshipEvidence,
        stage: "funding",
      },
      {
        id: "sponsorletter",
        label: "Letter of Sponsorship",
        description: "Official letter of sponsorship from sponsor(s)",
        accept: ".pdf,.doc,.docx",
        required: values.hasFinancialSponsors === true,
        uploaded: !!uploadedDocuments.sponsorLetter,
        stage: "funding",
      },
      {
        id: "sponsorcitizenship",
        label: "Sponsor's Citizenship/Identity Proof",
        description:
          "Sponsor's citizenship proof or identity documents (notarized/translated)",
        accept: ".pdf,.jpg,.jpeg",
        required: values.hasFinancialSponsors === true,
        uploaded: !!uploadedDocuments.sponsorCitizenship,
        stage: "funding",
      },
      {
        id: "sponsorvisa",
        label: "Sponsor's Visa Documents",
        description:
          "Sponsor's PR, Working Visa, or Temporary Visa documents (if in Australia)",
        accept: ".pdf,.jpg,.jpeg",
        required: values.hasFinancialSponsors === true,
        uploaded: !!uploadedDocuments.sponsorVisa,
        stage: "funding",
      },
      {
        id: "sponsorincometax",
        label: "Sponsor's Income Tax Returns",
        description: "Sponsor's income tax returns for last 3 fiscal years",
        accept: ".pdf",
        required: values.hasFinancialSponsors === true,
        uploaded: !!uploadedDocuments.sponsorIncomeTax,
        stage: "funding",
      },
      {
        id: "trainingcertificates",
        label: "Training & Workshop Certificates",
        description:
          "Certificates from relevant training programs or workshops",
        accept: ".pdf,.jpg,.jpeg",
        required: values.hasRelevantTraining === true,
        uploaded: !!uploadedDocuments.trainingCertificates,
        stage: "preparation",
      },
      {
        id: "oshcreceipt",
        label: "OSHC/OVHC Receipt",
        description:
          "Overseas Student Health Cover or Overseas Visitor Health Cover receipt",
        accept: ".pdf,.jpg,.jpeg",
        required: values.healthInsuranceArranged === true,
        uploaded: !!uploadedDocuments.oshcReceipt,
        stage: "preparation",
      },
      {
        id: "dependentoshc",
        label: "Dependent's Health Insurance",
        description: "Health insurance coverage for dependent(s)",
        accept: ".pdf,.jpg,.jpeg",
        required: values.bringingDependents === true,
        uploaded: !!uploadedDocuments.dependentOSHC,
        stage: "preparation",
      },
    ];

    if (filterByStage) {
      return allDocuments.filter(
        (doc) => doc.stage === filterByStage && doc.required,
      );
    }

    return allDocuments;
  };

  const isSponsorComplete = (num: number): boolean => {
    const familyName = values[
      `sponsor${num}FamilyName` as keyof GenuineStudentFormValues
    ] as string;
    const givenName = values[
      `sponsor${num}GivenName` as keyof GenuineStudentFormValues
    ] as string;
    const country = values[
      `sponsor${num}Country` as keyof GenuineStudentFormValues
    ] as string;
    const relationship = values[
      `sponsor${num}Relationship` as keyof GenuineStudentFormValues
    ] as string;
    const occupation = values[
      `sponsor${num}Occupation` as keyof GenuineStudentFormValues
    ] as string;
    const income = values[
      `sponsor${num}Income` as keyof GenuineStudentFormValues
    ] as number;
    const currency = values[
      `sponsor${num}Currency` as keyof GenuineStudentFormValues
    ] as string;

    const hasAnyField =
      familyName ||
      givenName ||
      country ||
      relationship ||
      occupation ||
      income ||
      currency;
    if (!hasAnyField) return false;

    return !!(
      familyName &&
      givenName &&
      country &&
      relationship &&
      occupation &&
      income &&
      currency
    );
  };

  const renderSponsorForm = (num: number) => {
    const familyNameKey =
      `sponsor${num}FamilyName` as keyof GenuineStudentFormValues;
    const givenNameKey =
      `sponsor${num}GivenName` as keyof GenuineStudentFormValues;
    const countryKey = `sponsor${num}Country` as keyof GenuineStudentFormValues;
    const relationshipKey =
      `sponsor${num}Relationship` as keyof GenuineStudentFormValues;
    const occupationKey =
      `sponsor${num}Occupation` as keyof GenuineStudentFormValues;
    const incomeKey = `sponsor${num}Income` as keyof GenuineStudentFormValues;
    const currencyKey =
      `sponsor${num}Currency` as keyof GenuineStudentFormValues;

    return (
      <div className="sponsor-form">
        <div className="sponsor-row">
          <TextInput
            id={familyNameKey}
            label="Family Name"
            placeholder="Enter family name"
            getFieldProps={() => ({
              value: (values[familyNameKey] as string) || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors[familyNameKey] as string}
            touched={touched[familyNameKey] as boolean}
          />

          <TextInput
            id={givenNameKey}
            label="Given Name"
            placeholder="Enter given name"
            getFieldProps={() => ({
              value: (values[givenNameKey] as string) || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors[givenNameKey] as string}
            touched={touched[givenNameKey] as boolean}
          />
        </div>

        <div className="sponsor-row">
          <GeneralSelectInput
            id={countryKey}
            label="Country"
            options={countriesFlat}
            value={(values[countryKey] as string) || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors[countryKey] as string}
            touched={touched[countryKey] as boolean}
          />

          <GeneralSelectInput
            id={relationshipKey}
            label="Relationship"
            options={sponsorRelationshipOptions}
            value={(values[relationshipKey] as string) || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors[relationshipKey] as string}
            touched={touched[relationshipKey] as boolean}
          />
        </div>

        <TextInput
          id={occupationKey}
          label="Occupation"
          placeholder="Enter occupation"
          getFieldProps={() => ({
            value: (values[occupationKey] as string) || "",
            onChange: handleChange,
            onBlur: handleBlur,
          })}
          error={errors[occupationKey] as string}
          touched={touched[occupationKey] as boolean}
        />

        <div className="sponsor-row">
          <NumberInput
            id={incomeKey}
            label="Annual Income"
            placeholder="Enter amount"
            getFieldProps={() => ({
              value: (values[incomeKey] as number) || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors[incomeKey] as string}
            touched={touched[incomeKey] as boolean}
          />

          <GeneralSelectInput
            id={currencyKey}
            label="Currency"
            options={currencyOptions}
            value={(values[currencyKey] as string) || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors[currencyKey] as string}
            touched={touched[currencyKey] as boolean}
          />
        </div>
      </div>
    );
  };

  const renderFundingSection = () => (
    <div className="funding-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Funding</h4>
        </div>
        <div className="form-fields">
          <GeneralSelectInput
            id="primaryFundingSource"
            label="Primary funding source:"
            options={primaryFundingOptions}
            value={values.primaryFundingSource || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.primaryFundingSource}
            touched={touched.primaryFundingSource}
          />
          {values.primaryFundingSource === "other" && (
            <TextInput
              id="otherPrimaryFundingSource"
              label=""
              placeholder="Please specify."
              getFieldProps={() => ({
                value: values.otherPrimaryFundingSource || "",
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={errors.otherPrimaryFundingSource}
              touched={touched.otherPrimaryFundingSource}
            />
          )}

          <RadioButtonGroup
            name="fundingFullySecured"
            label="Is your funding fully secured?"
            value={values.fundingFullySecured}
            onChange={(value) => {
              setFieldValue("fundingFullySecured", value);
              setFieldTouched("fundingFullySecured", true);
            }}
          />

          <div className="form-field">
            <NumberInput
              id="totalFundsAvailable"
              label="Estimated total funds available:"
              placeholder="Enter amount"
              getFieldProps={() => ({
                value: values.totalFundsAvailable || "",
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={errors.totalFundsAvailable}
              touched={touched.totalFundsAvailable}
            />

            <GeneralSelectInput
              id="totalFundsCurrency"
              label="Currency:"
              options={currencyOptions}
              value={values.totalFundsCurrency || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.totalFundsCurrency}
              touched={touched.totalFundsCurrency}
            />
          </div>

          {(values.primaryFundingSource === "scholarship" ||
            values.primaryFundingSource === "government_loan" ||
            values.primaryFundingSource === "bank_loan") && (
            <RadioButtonGroup
              name="scholarshipLoanApproved"
              label="Has the scholarship/loan been officially approved?"
              value={values.scholarshipLoanApproved}
              onChange={(value) => {
                setFieldValue("scholarshipLoanApproved", value);
                setFieldTouched("scholarshipLoanApproved", true);
              }}
            />
          )}

          <div className="form-field">
            <RadioButtonGroup
              name="hasFinancialSponsor"
              label="Do you have financial sponsor(s)?"
              value={values.hasFinancialSponsors}
              onChange={(value) => {
                setFieldValue("hasFinancialSponsors", value);
                setFieldTouched("hasFinancialSponsors", true);
                if (value === false) {
                  for (let i = 1; i <= 4; i++) {
                    setFieldValue(`sponsor${i}FamilyName`, "");
                    setFieldValue(`sponsor${i}GivenName`, "");
                    setFieldValue(`sponsor${i}Country`, "");
                    setFieldValue(`sponsor${i}Relationship`, "");
                    setFieldValue(`sponsor${i}Occupation`, "");
                    setFieldValue(`sponsor${i}Income`, 0);
                    setFieldValue(`sponsor${i}Currency`, "");
                  }
                }
              }}
            />

            {values.hasFinancialSponsors === true && (
              <TabbedFormToggle
                displayTabs={[
                  {
                    id: "sponsor1",
                    label: "Sponsor 1",
                    component: renderSponsorForm(1),
                  },
                  {
                    id: "sponsor2",
                    label: "Sponsor 2",
                    component: renderSponsorForm(2),
                  },
                  {
                    id: "sponsor3",
                    label: "Sponsor 3",
                    component: renderSponsorForm(3),
                  },
                  {
                    id: "sponsor4",
                    label: "Sponsor 4",
                    component: renderSponsorForm(4),
                  },
                ]}
                formikInstance={null}
                className="sponsor-tabs"
                isSubStageComplete={(tabId) => {
                  const num = parseInt(tabId.replace("sponsor", ""));
                  return isSponsorComplete(num);
                }}
              />
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="hasFinancialObligationsHome"
              label="Do you have financial obligations in your home country requiring income?"
              value={values.hasFinancialObligationsHome}
              onChange={(value) => {
                setFieldValue("hasFinancialObligationsHome", value);
                setFieldTouched("hasFinancialObligationsHome", true);
                if (value === false) {
                  setFieldValue("financialObligationTypes", []);
                }
              }}
            />
            {values.hasFinancialObligationsHome === true && (
              <MultiChoiceCheckboxGroup
                name="financialObligationTypes"
                label="Select all that apply:"
                options={financialObligationTypesOptions}
                value={values.financialObligationTypes || []}
                onChange={(values: string[]) =>
                  setFieldValue("financialObligationTypes", values)
                }
              />
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="hasIncomeGeneratingAssets"
              label="Will you have income-generating assets during your study?"
              value={values.hasIncomeGeneratingAssets}
              onChange={(value) => {
                setFieldValue("hasIncomeGeneratingAssets", value);
                setFieldTouched("hasIncomeGeneratingAssets", true);
                if (value === false) {
                  setFieldValue("incomeGeneratingAssetTypes", []);
                }
              }}
            />
            {values.hasIncomeGeneratingAssets === true && (
              <MultiChoiceCheckboxGroup
                name="incomeGeneratingAssetTypes"
                label="Select all that apply:"
                options={incomeGeneratingAssetTypesOptions}
                value={values.incomeGeneratingAssetTypes || []}
                onChange={(values: string[]) =>
                  setFieldValue("incomeGeneratingAssetTypes", values)
                }
                onOtherChange={(value) =>
                  setFieldValue("otherIncomeGeneratingAssetTypes", value)
                }
                showOther={true}
                otherPlaceholder="Please specify"
                required={true}
              />
            )}
          </div>

          <MessageTextAreaInput
            id="financialCapacity"
            label="Financial Capacity (150 words max)"
            placeholder="Describe your financial capacity to undertake this study. Detail your funding sources, amounts available, and how you will meet tuition fees and living costs. If using family support or sponsorship, explain their financial capacity. Include any financial ties to your home country that will support your return."
            rows="6"
            maxLength={150}
            getFieldProps={() => ({
              value: values.financialCapacity || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.financialCapacity}
            touched={touched.financialCapacity}
          />

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Financial Documents"
        description="Please attach financial supporting documents:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderPreparationSection = () => (
    <div className="preparation-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Research & Understanding</h4>
        </div>
        <div className="form-fields">
          <MultiChoiceCheckboxGroup
            name="researchConductedActivities"
            label="Research conducted: (Check all that apply)"
            options={researchConductedFullOptions}
            value={values.researchConductedActivities || []}
            onChange={(values: string[]) =>
              setFieldValue("researchConductedActivities", values)
            }
          />

          <div className="form-field">
            <RadioButtonGroup
              name="researchedLivingCosts"
              label="Have you researched living costs in the host country?"
              value={values.researchedLivingCosts}
              onChange={(value) => {
                setFieldValue("researchedLivingCosts", value);
                setFieldTouched("researchedLivingCosts", true);
                if (value === false) {
                  setFieldValue("estimatedMonthlyExpenses", null);
                  setFieldValue("estimatedExpensesCurrency", "");
                }
              }}
            />
            {values.researchedLivingCosts === true && (
              <>
                <NumberInput
                  id="estimatedMonthlyExpenses"
                  label="Estimated monthly living expenses:"
                  placeholder="Enter amount"
                  getFieldProps={() => ({
                    value: values.estimatedMonthlyExpenses || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.estimatedMonthlyExpenses}
                  touched={touched.estimatedMonthlyExpenses}
                />

                <GeneralSelectInput
                  id="estimatedExpensesCurrency"
                  label="Currency:"
                  options={currencyOptions}
                  value={values.estimatedExpensesCurrency || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.estimatedExpensesCurrency}
                  touched={touched.estimatedExpensesCurrency}
                />
              </>
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="arrangedAccommodation"
              label="Have you arranged accommodation?"
              value={values.arrangedAccommodation}
              onChange={(value) => {
                setFieldValue("arrangedAccommodation", value);
                setFieldTouched("arrangedAccommodation", true);
                if (value === false) {
                  setFieldValue("accommodationType", "");
                }
              }}
            />
            {values.arrangedAccommodation === true && (
              <GeneralSelectInput
                id="accommodationType"
                label="Accommodation type:"
                options={accommodationTypeOptions}
                value={values.accommodationType || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.accommodationType}
                touched={touched.accommodationType}
              />
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="hasRelevantTraining"
              label="Have you completed any relevant training or workshops?"
              value={values.hasRelevantTraining}
              onChange={(value) => {
                setFieldValue("hasRelevantTraining", value);
                setFieldTouched("hasRelevantTraining", true);
                if (value === false) {
                  setFieldValue("trainingDetails", "");
                }
              }}
            />
            {values.hasRelevantTraining === true && (
              <MessageTextAreaInput
                id="trainingDetails"
                label="Training Details (100 words max)"
                placeholder="Describe the training or workshops you've completed and how they relate to your study plans."
                rows="4"
                maxLength={100}
                getFieldProps={() => ({
                  value: values.trainingDetails || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.trainingDetails}
                touched={touched.trainingDetails}
              />
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="healthInsuranceArranged"
              label="Have you arranged Overseas Student Health Cover (OSHC)?"
              value={values.healthInsuranceArranged}
              onChange={(value) => {
                setFieldValue("healthInsuranceArranged", value);
                setFieldTouched("healthInsuranceArranged", true);
                if (value === false) {
                  setFieldValue("healthInsuranceProvider", "");
                  setFieldValue("healthInsuranceDuration", "");
                }
              }}
            />
            {values.healthInsuranceArranged === true && (
              <>
                <GeneralSelectInput
                  id="healthInsuranceProvider"
                  label="Health insurance provider:"
                  options={healthInsuranceProviderOptions}
                  value={values.healthInsuranceProvider || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.healthInsuranceProvider}
                  touched={touched.healthInsuranceProvider}
                />
                {values.healthInsuranceProvider === "other" && (
                  <TextInput
                    id="otherHealthInsuranceProvider"
                    label="Please specify:"
                    placeholder="Please specify"
                    getFieldProps={() => ({
                      value: values.otherHealthInsuranceProvider || "",
                      onChange: handleChange,
                      onBlur: handleBlur,
                    })}
                    error={errors.otherHealthInsuranceProvider}
                    touched={touched.otherHealthInsuranceProvider}
                  />
                )}

                <TextInput
                  id="healthInsuranceDuration"
                  label="Coverage duration:"
                  placeholder="e.g., 12 months, 24 months"
                  getFieldProps={() => ({
                    value: values.healthInsuranceDuration || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.healthInsuranceDuration}
                  touched={touched.healthInsuranceDuration}
                />
              </>
            )}
          </div>

          <div className="understanding-level-group">
            <label className="section-label">Rate your understanding:</label>

            <GeneralSelectInput
              id="understandingCourseRequirements"
              label="Course academic requirements:"
              options={understandingLevelOptions}
              value={values.understandingCourseRequirements || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.understandingCourseRequirements}
              touched={touched.understandingCourseRequirements}
            />

            <GeneralSelectInput
              id="understandingLivingCosts"
              label="Living costs:"
              options={understandingLevelOptions}
              value={values.understandingLivingCosts || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.understandingLivingCosts}
              touched={touched.understandingLivingCosts}
            />

            <GeneralSelectInput
              id="understandingVisaConditions"
              label="Visa conditions:"
              options={understandingLevelOptions}
              value={values.understandingVisaConditions || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.understandingVisaConditions}
              touched={touched.understandingVisaConditions}
            />

            <GeneralSelectInput
              id="understandingWorkRestrictions"
              label="Work restrictions:"
              options={understandingLevelOptions}
              value={values.understandingWorkRestrictions || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.understandingWorkRestrictions}
              touched={touched.understandingWorkRestrictions}
            />
          </div>

          <MessageTextAreaInput
            id="studyLivingKnowledge"
            label="Study & Living Knowledge (150 words max)"
            placeholder="Describe your understanding of the course requirements and what you know about living in the host country. Include specific details about course structure, workload expectations, living costs, accommodation, culture, and any challenges you anticipate and how you'll address them."
            rows="6"
            maxLength={150}
            getFieldProps={() => ({
              value: values.studyLivingKnowledge || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.studyLivingKnowledge}
            touched={touched.studyLivingKnowledge}
          />

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Documents"
        description="Please attach supporting documents:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderReadinessSection = () => (
    <div className="readiness-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Language & Readiness</h4>
        </div>
        <div className="form-fields">
          <GeneralSelectInput
            id="englishTestTaken"
            label="English language test taken:"
            options={getTestOptions()}
            value={values.englishTestTaken || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.englishTestTaken}
            touched={touched.englishTestTaken}
          />

          {values.englishTestTaken && values.englishTestTaken !== "none" && (
            <>
              <NumberInput
                id="englishTestScore"
                label="Overall score:"
                placeholder="Enter your score"
                getFieldProps={() => ({
                  value: values.englishTestScore || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.englishTestScore}
                touched={touched.englishTestScore}
              />
              <div className="english-test-date">
                <DatePickerInput
                  id="englishTestDate"
                  label="Test date:"
                  value={values.englishTestDate}
                  onChange={(date) => setFieldValue("englishTestDate", date)}
                  preset="pastDate"
                />
              </div>
            </>
          )}

          <div className="form-field">
            <RadioButtonGroup
              name="hasEducationEmploymentGaps"
              label="Have you had education or employment gaps longer than 6 months?"
              value={values.hasEducationEmploymentGaps}
              onChange={(value) => {
                setFieldValue("hasEducationEmploymentGaps", value);
                setFieldTouched("hasEducationEmploymentGaps", true);
                if (value === false) {
                  setFieldValue("gapPrimaryActivity", "");
                  setFieldValue("gapPeriodExplanation", "");
                }
              }}
            />
            {values.hasEducationEmploymentGaps === true && (
              <>
                <GeneralSelectInput
                  id="gapPrimaryActivity"
                  label="Primary activity during gap:"
                  options={gapActivityOptions}
                  value={values.gapPrimaryActivity || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.gapPrimaryActivity}
                  touched={touched.gapPrimaryActivity}
                />
                {values.gapPrimaryActivity === "other" && (
                  <TextInput
                    id="otherGapPrimaryActivity"
                    label=""
                    placeholder="Please specify."
                    getFieldProps={() => ({
                      value: values.otherGapPrimaryActivity || "",
                      onChange: handleChange,
                      onBlur: handleBlur,
                    })}
                    error={errors.otherGapPrimaryActivity}
                    touched={touched.otherGapPrimaryActivity}
                  />
                )}

                <MessageTextAreaInput
                  id="gapPeriodExplanation"
                  label="Gap Period Explanation (150 words max)"
                  placeholder="Explain your activities during any gaps in education or employment. Describe how you used this time productively and how these experiences have prepared you for this study or strengthened your application."
                  rows="6"
                  maxLength={150}
                  getFieldProps={() => ({
                    value: values.gapPeriodExplanation || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.gapPeriodExplanation}
                  touched={touched.gapPeriodExplanation}
                />
              </>
            )}
          </div>

          <MultiChoiceCheckboxGroup
            name="applicationPreparation"
            label="How did you prepare for this application? (Check all that apply)"
            options={preparationActivitiesOptions}
            value={values.applicationPreparation || []}
            onChange={(values: string[]) =>
              setFieldValue("applicationPreparation", values)
            }
          />
        </div>
      </div>
    </div>
  );

  switch (currentSubStage) {
    case "funding":
      return renderFundingSection();
    case "preparation":
      return renderPreparationSection();
    case "readiness":
      return renderReadinessSection();
    default:
      return renderFundingSection();
  }
};

export default GSFinancialPreparationSection;
